"use client";

import { Star, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import type { SearchParams } from "@/types";

const ratingOptions = [4.5, 4, 3, 2, 1];

const EMPTY_CATEGORIES: string[] = [];

export function SearchSidebar({ 
  params, 
  initialCategories = EMPTY_CATEGORIES,
  onFilterApplied,
  className
}: { 
  params: SearchParams; 
  initialCategories?: string[];
  onFilterApplied?: () => void;
  className?: string;
}) {
  const [categories, setCategories] = useState<string[]>(initialCategories);
  const [loading, setLoading] = useState(initialCategories.length === 0);

  useEffect(() => {
    let active = true;
    async function loadCategories() {
      try {
        const res = await fetch("/api/categories", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (active && Array.isArray(data.categories)) {
          setCategories(data.categories);
        }
      } catch (err) {
        console.error("Failed to fetch categories from backend:", err);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadCategories();
    window.addEventListener("nav-updated", loadCategories);
    return () => {
      active = false;
      window.removeEventListener("nav-updated", loadCategories);
    };
  }, []);

  const categoriesKey = initialCategories.join(",");
  useEffect(() => {
    if (initialCategories.length > 0) {
      setCategories(initialCategories);
    }
  }, [categoriesKey]);

  const activeCategory = params.category && params.category !== "All" ? params.category : null;
  const activeRating = params.rating ? Number(params.rating) : null;

  const displayCategories = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const c of categories) {
      if (!c?.trim()) continue;
      const lower = c.trim().toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        list.push(c.trim());
      }
    }
    return list;
  }, [categories]);

  function withParams(
    overrides: Partial<
      Record<"category" | "collection" | "rating" | "prime", string | undefined>
    >
  ) {
    const next = new URLSearchParams();
    if (params.q) next.set("q", params.q);
    if (params.sort) next.set("sort", params.sort);

    const category = "category" in overrides ? overrides.category : params.category;
    const collection = "collection" in overrides ? overrides.collection : params.collection;
    const rating = "rating" in overrides ? overrides.rating : params.rating;
    const prime = "prime" in overrides ? overrides.prime : params.prime;

    if (category && category !== "All") next.set("category", category);
    if (collection) next.set("collection", collection);
    if (rating) next.set("rating", rating);
    if (prime) next.set("prime", prime);

    const query = next.toString();
    return `/search${query ? `?${query}` : ""}`;
  }

  const hasFilters = Boolean(activeCategory || params.collection || activeRating || params.prime);

  return (
    <aside className={cn("w-full shrink-0 lg:w-60", className)}>
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xs dark:border-white/10 dark:bg-slate-900">
        <div className="mb-3.5 flex items-center justify-between">
          <h2 className="text-base font-bold text-[#073B70] dark:text-white">Filters</h2>
          {hasFilters && (
            <Link 
              href="/search" 
              onClick={() => onFilterApplied?.()}
              className="flex items-center gap-1 text-xs font-bold text-[#087DB5] hover:text-[#073B70] hover:underline"
            >
              <X className="h-3 w-3" />
              Clear all
            </Link>
          )}
        </div>

        {params.collection && (
          <div className="mb-3 flex items-center justify-between rounded-xl bg-purple-50/80 px-3 py-2 text-xs text-purple-900 dark:bg-purple-950/40 dark:text-purple-200 border border-purple-200 dark:border-purple-800">
            <span className="font-bold truncate">Collection: {params.collection}</span>
            <Link
              href={withParams({ collection: undefined })}
              onClick={() => onFilterApplied?.()}
              className="ml-1 rounded p-0.5 text-purple-500 hover:bg-purple-100 hover:text-purple-800 dark:hover:bg-purple-900 dark:hover:text-purple-100"
              title="Clear collection filter"
            >
              <X className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        <div className="border-t border-slate-100 py-3.5 dark:border-white/10">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-[#073B70] dark:text-white">Category</h3>
          {loading && categories.length === 0 ? (
            <div className="space-y-2 py-1">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 w-3/4 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
              ))}
            </div>
          ) : (
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link
                  href={withParams({ category: undefined })}
                  onClick={() => onFilterApplied?.()}
                  className={cn(
                    "block text-slate-700 hover:text-[#087DB5] transition-colors dark:text-slate-300",
                    !activeCategory ? "font-bold text-[#087DB5] dark:text-sky-300" : ""
                  )}
                >
                  All categories
                </Link>
              </li>
              {displayCategories.map((category: string) => {
                const isSelected = activeCategory?.toLowerCase() === category.toLowerCase();
                return (
                  <li key={category}>
                    <Link
                      href={withParams({ category })}
                      onClick={() => onFilterApplied?.()}
                      className={cn(
                        "block text-slate-700 hover:text-[#087DB5] transition-colors dark:text-slate-300",
                        isSelected ? "font-bold text-[#087DB5] dark:text-sky-300" : ""
                      )}
                    >
                      {category}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-slate-100 py-3.5 dark:border-white/10">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-[#073B70] dark:text-white">Delivery & Assurance</h3>
          <Link
            href={withParams({ prime: params.prime ? undefined : "1" })}
            onClick={() => onFilterApplied?.()}
            className={cn(
              "flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm hover:bg-sky-50 transition-colors dark:hover:bg-white/5",
              params.prime && "bg-sky-50 font-bold dark:bg-white/10"
            )}
          >
            <span className="rounded-md bg-gradient-to-r from-[#073B70] to-[#087DB5] px-1.5 py-0.5 text-[10px] font-bold uppercase text-white shadow-xs">MOVA</span>
            <span>Fast Express Delivery</span>
          </Link>
        </div>

        <div className="border-t border-slate-100 py-3.5 dark:border-white/10">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-[#073B70] dark:text-white">Customer Rating</h3>
          <ul className="space-y-1.5">
            {ratingOptions.map((rating) => (
              <li key={rating}>
                <Link
                  href={withParams({ rating: activeRating === rating ? undefined : String(rating) })}
                  onClick={() => onFilterApplied?.()}
                  className={cn(
                    "flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm hover:bg-slate-50 transition-colors dark:hover:bg-white/5",
                    activeRating === rating && "bg-sky-50 font-bold dark:bg-white/10"
                  )}
                >
                  <span className="flex items-center">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={index}
                        className={cn(
                          "h-3.5 w-3.5",
                          index + 1 <= Math.floor(rating) ? "fill-[#FF8A00] text-[#FF8A00]" : "fill-slate-200 text-slate-200"
                        )}
                      />
                    ))}
                  </span>
                  <span className="text-slate-700 dark:text-slate-300">& Up</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

